import React, { useRef, useState, useEffect, useCallback } from "react";
import { Delete } from "lucide-react";

export function Wordmark({ className = "", sub = true }) {
  return (
    <div className={className}>
      <div className="wordmark text-3xl text-[color:var(--olive)]" data-testid="wordmark">PATRIA</div>
      {sub && <div className="text-[0.6rem] tracking-[0.35em] text-[color:var(--muted)] uppercase mt-0.5">Count &amp; Deposit</div>}
    </div>
  );
}

export function NumField({ label, value, onChange, prefix, testid, placeholder = "0", ...rest }) {
  return (
    <label className="block">
      {label && <span className="label block mb-1.5">{label}</span>}
      <span className="relative block">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--muted)] font-mono">{prefix}</span>}
        <input
          className={`field font-mono tabnum ${prefix ? "pl-7" : ""}`}
          inputMode="numeric"
          pattern="[0-9]*"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
          data-testid={testid}
          {...rest}
        />
      </span>
    </label>
  );
}

export function PinPad({ onComplete, length = 6, testid = "pinpad" }) {
  const [pin, setPin] = useState("");
  const press = (d) => {
    if (pin.length >= length) return;
    const next = pin + d;
    setPin(next);
    if (next.length === length) onComplete?.(next);
  };
  const back = () => setPin((p) => p.slice(0, -1));
  return (
    <div data-testid={testid}>
      <div className="flex justify-center gap-3 mb-6">
        {Array.from({ length }).map((_, i) => (
          <div key={i} className={`w-4 h-4 rounded-full border ${i < pin.length ? "bg-[color:var(--olive)] border-[color:var(--olive)]" : "border-[color:var(--line)]"}`} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button key={n} className="btn btn-secondary h-16 text-2xl font-mono" onClick={() => press(String(n))} data-testid={`${testid}-${n}`}>{n}</button>
        ))}
        <button className="btn btn-ghost h-16" onClick={() => setPin("")} data-testid={`${testid}-clear`}>C</button>
        <button className="btn btn-secondary h-16 text-2xl font-mono" onClick={() => press("0")} data-testid={`${testid}-0`}>0</button>
        <button className="btn btn-ghost h-16" onClick={back} data-testid={`${testid}-back`}><Delete size={22} /></button>
      </div>
    </div>
  );
}

export function SignaturePad({ onChange, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1a1c18";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); setEmpty(false); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange?.(canvasRef.current.toDataURL("image/png")); };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setEmpty(true); onChange?.(null); };

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="w-full h-44 bg-white rounded-lg border border-[color:var(--line)] touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid={testid}
      />
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-[color:var(--muted)]">{empty ? "Sign above" : "Signature captured"}</span>
        <button className="btn btn-ghost h-9 px-4 text-sm" onClick={clear} data-testid={`${testid}-clear`}>Clear</button>
      </div>
    </div>
  );
}
